import React, { useState } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useParams } from 'react-router-dom';
import { Typography , Box , Divider } from '@mui/material';

import Header from 'components/Analytics/Header'
import ScreenHeading from 'components/ScreenHeading'
import Tabs from 'components/Tabs'
import Usage from 'components/Analytics/Usage'
import Trends from 'components/Analytics/Trends'
import BatchHighlighting from 'components/Analytics/BatchHighlighting'
import LeaderBoard from 'components/Analytics/LeaderBoard'
import RecentTemplatesAnalytics from 'components/BatchAnalytics/RecentTemplatesAnalytics';
import StudentsPerformanceList from 'components/Analytics/StudentsPerformanceList';
import { SimpleTabs } from 'components/Tabs'
import { WhiteBox } from 'components/AppShell'
import { getBatchById } from 'services';
import { getLeaderboard } from 'services/analyticsV2';
 
const CHALLENGES = [
  { _id: 'assessment', label: 'Assessment' },
  { _id: 'mockInterview', label: 'Mock Interview' },
  { _id: 'interviewWizard', label: 'Interview Wizard' },
];

const TAB_KEYS = CHALLENGES.map(challenge => challenge._id);

const TOGGLE_OPTIONS = [
  { _id: 'nextRoundMock', label: 'Verbal' },
  { _id: 'codingMock', label: 'Coding' },
]

function InfoBox() {
  return (
    <Box
      display="flex"
      alignItems="center"
      bgcolor="#F2FAFF"
      borderRadius={2}
      px={2}
      py={1.5}
      mt={1}
      width="50%"
    >
      <InfoOutlinedIcon sx={{ color: 'primary.clr-600', fontSize: 16, mr: 1 }} />
      <Typography
        variant="body01-semiBold"
        color="neutral.clr-600"
      >
        Use the filter to view top 5 performers in "High Scorers" and "Most Dedicated" categories.
      </Typography>
    </Box>
  );
}

function BatchAnalytics() {
  const { id } = useParams();

  const [_batch, setBatch] = React.useState({});
  const [activeTab, setActiveTab] = React.useState('interviewWizard');
  const [section, setSection] = React.useState('nextRoundMock');
  const [leaderboardCategory, setLeaderboardCategory] = useState('best_score');

  const [leaderboardData, setLeaderboardData] = React.useState({ topPerformers: [], bestPerformers: [] });
  const [leaderboardLoading, setLeaderboardLoading] = React.useState(true);
  const [leaderboardError, setLeaderboardError] = React.useState(null);

  const interviewVariantType = React.useMemo(() => {
    if (activeTab === 'mockInterview') {
      return section === 'nextRoundMock' ? 'verbal_mock' : 'coding_mock';
    }
    if (activeTab === 'interviewWizard') {
      return section === 'nextRoundMock' ? 'verbal_iw' : 'coding_iw';
    }
    return 'assessment';
  }, [activeTab, section]);

  const handleTabChange = (tabId) => {
    setActiveTab(TAB_KEYS[tabId]);
    setSection('nextRoundMock');
  };

  const handleToggleChange = (toggleId) => {
    setSection(toggleId);
  };

  const simpleTabsOptions = React.useMemo(
    () => CHALLENGES.map(({ _id, label }) => ({ id: _id, label })),
    []
  );

  React.useEffect(() => {
    getBatchById(id, { domain: true })
      .then(template => setBatch(template))
      .catch(console.error)
  }, [id]);

  React.useEffect(() => {
    if (!id || !interviewVariantType) return;
    setLeaderboardLoading(true);
    setLeaderboardError(null);
    
    const params = { type: 'batch', id, interviewVariantType };
      params.leaderboardType = leaderboardCategory;
    
    getLeaderboard(params)
      .then(response => {
          setLeaderboardData(response || []);
      })
      .catch(err => setLeaderboardError(err.message))
      .finally(() => setLeaderboardLoading(false));
  }, [id, interviewVariantType, leaderboardCategory]);

  const renderHeaderAndLeaderboards = (variantType) => (
    <>
      <Box display="flex" gap={4} mt={2}>
        <LeaderBoard 
          data={leaderboardData} 
          loading={leaderboardLoading} 
          error={leaderboardError} 
          leaderboardCategory={leaderboardCategory} 
          setLeaderboardCategory={setLeaderboardCategory} 
          interviewVariantType={variantType} 
          batchId={id}
        />
        <Usage batchId={id} interviewVariantType={variantType} />
      </Box>
      <InfoBox />
      <Divider />
      <Trends batchId={id} interviewVariantType={variantType} />
      <Divider />
      <BatchHighlighting batchId={id} interviewVariantType={variantType} />
    </>
  );

  return (
    <Box display="flex" flexDirection="column" minHeight='calc(100vh - 76px)'>
       <ScreenHeading title="Batch Performance" id="batches-performance"></ScreenHeading>
      <WhiteBox>
        <Header batchId={id} />
        <Box display="flex" flexDirection="column" gap={7} mt={3}>
          <SimpleTabs
            options={simpleTabsOptions.map((opt, idx) => ({ ...opt, value: idx }))}
            value={TAB_KEYS.indexOf(activeTab)}
            onChange={handleTabChange}
          />

          {activeTab === 'assessment' && (
            <Box display="flex" flexDirection="column" gap={9}>
              {renderHeaderAndLeaderboards('assessment')}
              <Divider />
              <RecentTemplatesAnalytics batchId={id} interviewVariantType="assessment" />
              <Divider />
              <StudentsPerformanceList batchId={id} interviewVariantType="assessment" />
            </Box>
          )}
          {(activeTab === 'mockInterview' || activeTab === 'interviewWizard') && (
            <Box display="flex" flexDirection="column" gap={9}>
              <Tabs
                options={TOGGLE_OPTIONS}
                value={section}
                onChange={handleToggleChange}
                width='180px'
              />
              {renderHeaderAndLeaderboards(interviewVariantType)}
              {activeTab !== 'interviewWizard' 
                && <>
                    <Divider />
                    <RecentTemplatesAnalytics 
                      batchId={id} 
                      interviewVariantType={interviewVariantType} 
                    />
                  </>
              }
              <Divider />
              <StudentsPerformanceList batchId={id} interviewVariantType={interviewVariantType} />
            </Box>
          )}
        </Box>
      </WhiteBox>
      </Box>
  );
}

export default BatchAnalytics;